import { useState } from 'react';
import { Save } from 'lucide-react';
import type {
  CreateFirewallRulesetVersionRequest,
  FirewallRulesetIssue,
  FirewallRulesetVersionRecord,
} from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';
import {
  firewallRuleDraftsFromRules,
  rulesFromRuleDrafts,
  validateRuleDrafts,
  type FirewallRuleDraft,
} from '../lib/firewallRuleDraft.ts';
import { FirewallIssueList } from './FirewallIssueList.tsx';
import { FirewallRuleListEditor } from './FirewallRuleListEditor.tsx';
import { Button, FormField, Input } from '../ui/index.ts';

/**
 * Edit a version's rule chain and save it as a NEW draft version (#1760).
 *
 * This is the whole shape of the design: there is no edit-in-place. Loading
 * v7, moving a rule up and saving produces v8 with `active_version` untouched,
 * so the edit is reviewable as a document and the switch is a separate,
 * separately-audited act. It also means every reorder inherits the activation
 * gate for free instead of needing one of its own.
 *
 * Mount with `key={version}` — the drafts are seeded once from the base
 * version, and a host that swapped the base underneath would otherwise show one
 * version's number over another version's rules.
 */
export function FirewallVersionEditor({
  baseVersion,
  pending,
  serverIssues,
  onSubmit,
}: {
  readonly baseVersion: FirewallRulesetVersionRecord;
  readonly pending: boolean;
  readonly serverIssues: readonly FirewallRulesetIssue[];
  readonly onSubmit: (body: CreateFirewallRulesetVersionRequest) => void;
}): JSX.Element {
  const t = useT();
  const initial = firewallRuleDraftsFromRules(baseVersion.rules);
  const [drafts, setDrafts] = useState<readonly FirewallRuleDraft[]>(initial ?? []);
  const [note, setNote] = useState('');

  if (initial === null) {
    // A stored document this editor cannot round-trip. It is still the live
    // policy, so it is reported rather than repaired: saving the editor's guess
    // at it would replace a working chain with a reconstruction nobody asked
    // for. Same posture as `bindingBlacklistState`'s diverged branch.
    return (
      <p
        className="m-0 rounded-lg border border-warn-line bg-warn-dim/30 p-3 text-label text-warn"
        role="alert"
        data-testid="firewall-version-editor-unreadable"
      >
        {t('firewall_rulesets.editor.unreadable')}
      </p>
    );
  }

  const localIssues = validateRuleDrafts(drafts);
  const submittable = drafts.length > 0 && localIssues.length === 0;

  function submit(): void {
    const serialized = rulesFromRuleDrafts(drafts);
    if (!serialized.ok) return;
    onSubmit({
      doc_version: baseVersion.doc_version,
      ...(baseVersion.extends_ruleset_id === null
        ? {}
        : { extends_ruleset_id: baseVersion.extends_ruleset_id }),
      rules: serialized.rules,
      ...(note.trim().length > 0 ? { note: note.trim() } : {}),
    });
  }

  return (
    <div className="flex flex-col gap-4" data-testid="firewall-version-editor">
      <p className="m-0 rounded-lg bg-well p-3 text-label text-paper-mid">
        {t('firewall_rulesets.editor.draft_note', { version: baseVersion.version })}
      </p>

      <FirewallRuleListEditor
        drafts={drafts}
        serverIssues={serverIssues}
        disabled={pending}
        idPrefix="firewall-version"
        onChange={setDrafts}
      />

      <FirewallIssueList issues={serverIssues} testId="firewall-version-editor-issues" />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <FormField
          label={t('firewall_rulesets.field.note')}
          fieldId="firewall-version-note"
          hint={t('firewall_rulesets.field.note_hint')}
          className="min-w-[16rem] flex-1"
        >
          <Input
            id="firewall-version-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            disabled={pending}
            data-testid="firewall-version-note"
          />
        </FormField>
        <Button
          variant="primary"
          onClick={submit}
          loading={pending}
          disabled={pending || !submittable}
          data-testid="firewall-version-save"
        >
          <Save aria-hidden="true" className="h-4 w-4" />
          <span>{t('firewall_rulesets.editor.save')}</span>
        </Button>
      </div>
    </div>
  );
}
